/**
 * Convert audio blob to WAV format using Web Audio API
 * @param audioBlob The audio blob to convert (can be WebM, MP3, etc.)
 * @returns Promise that resolves with a WAV format blob
 */
export const convertAudioToWav = async (audioBlob: Blob): Promise<Blob> => {
  try {
    // Create audio context
    const audioContext = new (globalThis.AudioContext ||
      (globalThis as any).webkitAudioContext)();

    // Decode the audio blob
    const arrayBuffer = await audioBlob.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

    // Convert AudioBuffer to WAV
    const wavBlob = audioBufferToWav(audioBuffer);

    return wavBlob;
  } catch (error) {
    console.error("Fout bij converteren audio naar WAV:", error);
    throw new Error(
      `Kon audio niet converteren naar WAV: ${(error as Error).message}`
    );
  }
};

/**
 * Convert AudioBuffer to WAV format blob
 * @param audioBuffer The AudioBuffer to convert
 * @returns WAV format blob
 */
function audioBufferToWav(audioBuffer: AudioBuffer): Blob {
  // Force mono and 16kHz for better Azure Speech Service compatibility
  const numChannels = 1; // Force mono
  const sampleRate = 16000; // Force 16kHz for Azure Speech Service
  const format = 1; // PCM
  const bitDepth = 16;

  const bytesPerSample = bitDepth / 8;
  const blockAlign = numChannels * bytesPerSample;

  // Resample to 16kHz if needed
  const targetLength = Math.floor((audioBuffer.length * sampleRate) / audioBuffer.sampleRate);
  const length = targetLength;
  const buffer = new ArrayBuffer(44 + length * numChannels * bytesPerSample);
  const view = new DataView(buffer);

  // WAV header
  const writeString = (offset: number, string: string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };

  writeString(0, "RIFF");
  view.setUint32(4, 36 + length * numChannels * bytesPerSample, true);
  writeString(8, "WAVE");
  writeString(12, "fmt ");
  view.setUint32(16, 16, true); // fmt chunk size
  view.setUint16(20, format, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitDepth, true);
  writeString(36, "data");
  view.setUint32(40, length * numChannels * bytesPerSample, true);

  // Convert and resample audio data
  let offset = 44;
  const resampleRatio = audioBuffer.length / length;
  const originalChannels = audioBuffer.numberOfChannels;
  
  for (let i = 0; i < length; i++) {
    // Calculate the corresponding sample in the original audio
    const originalIndex = Math.floor(i * resampleRatio);
    
    // Mix down to mono by averaging all channels
    let sample = 0;
    for (let channel = 0; channel < originalChannels; channel++) {
      const channelData = audioBuffer.getChannelData(channel);
      sample += channelData[originalIndex] || 0;
    }
    sample /= originalChannels;
    
    // Clamp and convert to 16-bit PCM
    sample = Math.max(-1, Math.min(1, sample));
    const pcmSample = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
    
    view.setInt16(offset, pcmSample, true);
    offset += 2;
  }

  return new Blob([buffer], { type: "audio/wav" });
}
